import { TRIAL_BADGE_WORD } from './organizationTrial';
import type { TrialStatus } from '../../types/admin';
import './OrganizationStatusBadge.css';

interface OrganizationStatusBadgeProps {
  isPaused: boolean;
  /**
   * The server's trial band. Optional so a row from an API that predates trials still renders —
   * it simply reads as Active, which is what it was before trials existed.
   */
  trialStatus?: TrialStatus | null;
}

/**
 * One-word status for an organization: Paused, a trial state, or Active.
 *
 * **The word is always shown, never colour alone.** Paused and "Trial ending" are both amber, and
 * the difference between them has to survive a reader who cannot tell those two ambers apart.
 *
 * **Paused takes precedence over any trial state.** A paused organization is locked out of
 * RosterPilot right now, which is the thing a system admin scanning this column needs to see;
 * where its trial happens to sit is a detail, and it is on the organization's own page. The
 * precedence also keeps the two ideas from being confused for one: an expired trial pauses
 * nothing in this epic, so a row reading "Paused" always means someone paused it by hand.
 *
 * `converted` and `none` both fall through to Active. Neither is a state of the organization —
 * one is a paying customer and the other never had a trial, and both are simply active. The
 * conversion date lives on the detail screen and in the list's own Trial column.
 */
export const OrganizationStatusBadge = ({
  isPaused,
  trialStatus,
}: OrganizationStatusBadgeProps) => {
  const trialWord = trialStatus ? TRIAL_BADGE_WORD[trialStatus] : null;

  const [modifier, word] = isPaused
    ? ['paused', 'Paused']
    : trialWord
      ? [`trial-${trialStatus}`, trialWord]
      : ['active', 'Active'];

  return (
    <span
      className={`org-status-badge org-status-badge--${modifier}`}
      data-testid="org-status-badge"
    >
      {word}
    </span>
  );
};
